import { catalog } from "../i18n.js";

/** Settings (components/Settings.tsx, ReviewSettings.tsx). */
export const SETTINGS = catalog(
  {
    title: "Settings",
    pagesLabel: "Settings pages",
    close: "Close",
    closeSettings: "Close settings",
    pageGeneral: "General",
    pageMachine: "Virtual Machine",
    pagePermissions: "Permissions",
    pageSkills: "Skills",

    account: "Account",
    accountAbout: "Your Ark Platform account. Your plan and its usage are managed in the platform portal.",
    logOut: "Log out",
    thisComputer: "This computer",
    thisComputerAbout: "Your bots run on this computer. There is no account to sign in to or out of.",

    language: "Language",
    appLanguage: "App language",
    appLanguageAbout: "The language of the app's menus, buttons and messages. Your bots reply in whatever language you write to them. This device keeps its own choice.",

    appearance: "Appearance",
    theme: "Theme",
    themeAbout: "Dark is the default. Light suits a bright room. This device keeps its own choice.",
    themeDark: "Dark",
    themeLight: "Light",
    style: "Style",
    styleAbout: "How the app is drawn: its colours, corners and type. Everything stays where it is in every style.",
    defaultChoice: "Default",
    fewerStyles: "Fewer styles",
    moreStyles: (hidden: number) => `More styles (${hidden})`,
    previewBots: "Bots",
    styleCalm: "Calm",
    styleCalmAbout: "Soft corners and quiet greys.",
    styleTactical: "Tactical",
    styleTacticalAbout: "Cut corners, condensed capitals, and hazard stripes on whatever waits on you.",
    styleIndustrial: "Industrial",
    styleIndustrialAbout: "Square corners and wide capitals on clean white or graphite, with signal yellow for what you pick.",
    styleVintage: "Vintage",
    styleVintageAbout: "Serif type on aged paper or library green, with brass, ticket-cut corners and typewritten labels.",
    styleStellar: "Stellar",
    styleStellarAbout: "Midnight navy or pearl, gold hairlines, classical capitals and a field of faint stars.",
    styleTerminal: "Terminal",
    styleTerminalAbout: "Monospace and square corners, as on a green screen at night or a printout by day.",
    styleStudio: "Studio",
    styleStudioAbout: "Warm greys, one orange and lower-case labels, like a Braun radio.",
    styleGlass: "Glass",
    styleGlassAbout: "See-through panes over a coloured ground.",

    timeZone: "Time zone",
    yourTimeZone: "Your time zone",
    timeZoneAbout: "Every time in the app is shown in this zone, and your bots use it when you ask about the time or set a schedule.",
    timeZoneUnset: "Not set yet: the app is showing this device’s zone, and your bots are working on UTC.",
    useZone: (zone: string) => `Use ${zone}`,

    dictation: "Dictation",
    dictationLanguage: "Language",
    dictationLanguageLabel: "Dictation language",
    dictationLanguageAbout: "The language the mic listens for. You can also change it from the bar that shows while you dictate.",
    autoSend: "Send when you stop speaking",
    autoSendAbout: "Off by default. When on, a dictated message is sent once you have been quiet for the time below, or when you press the mic, without pressing Send.",
    wait: "Wait before sending",
    waitAbout: "How long you can pause before the message goes. The mic keeps listening meanwhile, so speaking again carries on the same message.",
    waitNotHere: "Not used on this device: on Android, the phone decides when you have finished speaking, and the message is sent then.",
    waitNotOnAndroid: "Not used on Android (the app, or Chrome there): the phone decides when you have finished speaking, and the message is sent then.",
    seconds: (count: number) => `${count} ${count === 1 ? "second" : "seconds"}`,
    defaultMark: " (default)",
    dictationAvailable: "The words come from this device's own speech recognition; the audio never reaches your bots. This device keeps its own choices.",
    dictationUnavailable: "This browser cannot dictate, so the mic does not show here. Chrome, Edge and Safari can, on a page opened over https; so can the Android app.",

    gettingStarted: "Getting started",
    tour: "The tour",
    tourAbout: "A few minutes of practice: sending a message, answering an approval, watching your Chief of Staff delegate, then hiring a bot of your own.",
    takeTour: "Take the tour",

    machine: "Virtual machine",
    restart: "Restart",
    restarting: "Restarting…",
    restartAbout: "Backs up your workspace, then starts its machine again. Files and chats are kept; anything your bots are doing is interrupted.",
    restartConfirmTitle: "Restart your virtual machine?",
    restartConfirmMessage: "Anything your bots are doing now is interrupted. Your files and chats are kept.",
    restartLocalBefore: "This box runs on your own computer; restart it there, with ",
    restartLocalBetween: " and then ",
    restartLocalAfter: ".",

    reviewLead: "Some actions are checked before they run. Your rules below guide that check; they apply to every bot from the next action.",
    reviewEnabled: "Check actions before they run",
    reviewEnabledNote: "(off: only the fixed safety limits remain)",
    alwaysAllow: "Always allow",
    alwaysAllowNote: "(one sentence per line; what you never want to be asked about)",
    alwaysAllowExample: "Pushing to the origin remote of my projects is fine.\nInstalling packages from npm is fine.",
    alwaysBlock: "Always block",
    alwaysBlockNote: "(one sentence per line; what should always be brought to you)",
    alwaysBlockExample: "Never send email on my behalf.\nNever delete a git branch.",
    save: "Save",
    saved: "Saved",

    skillsLead: "Procedures every bot can follow, and the ones that belong to one bot.",
    skillsShared: "Shared",
    skillsSharedAbout: "Every bot sees these. Drop a folder with a SKILL.md into",
    skillsEmptyShared: "No shared skills yet.",
    skillsNewShared: "New shared skill",
    skillsNone: "No skills yet. Write a shared one here, or a bot's own from its details pane.",
    skillsOpen: (skill: string) => `Edit ${skill}`,
    skillsDeleteTitle: (skill: string) => `Delete the skill "${skill}"?`,
    skillsDeleteMessage: "It is no longer there to follow. This cannot be undone.",
  },
  {
    title: "Pengaturan",
    pagesLabel: "Halaman pengaturan",
    close: "Tutup",
    closeSettings: "Tutup pengaturan",
    pageGeneral: "Umum",
    pageMachine: "Mesin Virtual",
    pagePermissions: "Izin",
    pageSkills: "Keahlian",

    account: "Akun",
    accountAbout: "Akun Ark Platform kamu. Paket dan pemakaiannya diatur di portal platform.",
    logOut: "Keluar",
    thisComputer: "Komputer ini",
    thisComputerAbout: "Bot kamu berjalan di komputer ini. Tidak ada akun untuk masuk atau keluar.",

    language: "Bahasa",
    appLanguage: "Bahasa aplikasi",
    appLanguageAbout: "Bahasa untuk menu, tombol, dan pesan aplikasi. Bot kamu membalas dalam bahasa apa pun yang kamu pakai. Perangkat ini menyimpan pilihannya sendiri.",

    appearance: "Tampilan",
    theme: "Tema",
    themeAbout: "Gelap adalah bawaannya. Terang cocok untuk ruangan yang terang. Perangkat ini menyimpan pilihannya sendiri.",
    themeDark: "Gelap",
    themeLight: "Terang",
    style: "Gaya",
    styleAbout: "Cara aplikasi digambar: warna, sudut, dan hurufnya. Semua tetap di tempatnya di setiap gaya.",
    defaultChoice: "Bawaan",
    fewerStyles: "Lebih sedikit gaya",
    moreStyles: (hidden: number) => `Gaya lainnya (${hidden})`,
    previewBots: "Bot",
    styleCalm: "Tenang",
    styleCalmAbout: "Sudut lembut dan abu-abu yang kalem.",
    styleTactical: "Taktis",
    styleTacticalAbout: "Sudut terpotong, huruf kapital rapat, dan garis peringatan di hal yang menunggu kamu.",
    styleIndustrial: "Industri",
    styleIndustrialAbout: "Sudut siku dan huruf kapital lebar di atas putih bersih atau grafit, dengan kuning sinyal untuk pilihanmu.",
    styleVintage: "Vintage",
    styleVintageAbout: "Huruf serif di atas kertas tua atau hijau perpustakaan, dengan kuningan, sudut potongan tiket, dan label ketikan mesin tik.",
    styleStellar: "Bintang",
    styleStellarAbout: "Biru malam atau mutiara, garis tipis emas, huruf kapital klasik, dan hamparan bintang samar.",
    styleTerminal: "Terminal",
    styleTerminalAbout: "Huruf monospace dan sudut siku, seperti layar hijau di malam hari atau cetakan di siang hari.",
    styleStudio: "Studio",
    styleStudioAbout: "Abu-abu hangat, satu oranye, dan label huruf kecil, seperti radio Braun.",
    styleGlass: "Kaca",
    styleGlassAbout: "Panel tembus pandang di atas latar berwarna.",

    timeZone: "Zona waktu",
    yourTimeZone: "Zona waktu kamu",
    timeZoneAbout: "Semua waktu di aplikasi ditampilkan dalam zona ini, dan bot kamu memakainya saat kamu bertanya soal waktu atau membuat jadwal.",
    timeZoneUnset: "Belum diatur: aplikasi menampilkan zona perangkat ini, dan bot kamu bekerja dengan UTC.",
    useZone: (zone: string) => `Pakai ${zone}`,

    dictation: "Dikte",
    dictationLanguage: "Bahasa",
    dictationLanguageLabel: "Bahasa dikte",
    dictationLanguageAbout: "Bahasa yang didengarkan mikrofon. Kamu juga bisa menggantinya dari bilah yang muncul saat kamu mendikte.",
    autoSend: "Kirim saat kamu berhenti bicara",
    autoSendAbout: "Mati secara bawaan. Kalau nyala, pesan yang didikte langsung dikirim setelah kamu diam selama waktu di bawah, atau saat kamu menekan mikrofon, tanpa menekan Kirim.",
    wait: "Jeda sebelum mengirim",
    waitAbout: "Berapa lama kamu boleh berhenti sejenak sebelum pesannya terkirim. Mikrofon tetap mendengarkan, jadi kalau kamu bicara lagi, pesannya berlanjut.",
    waitNotHere: "Tidak dipakai di perangkat ini: di Android, ponsel yang menentukan kapan kamu selesai bicara, lalu pesannya dikirim.",
    waitNotOnAndroid: "Tidak dipakai di Android (aplikasi, atau Chrome di sana): ponsel yang menentukan kapan kamu selesai bicara, lalu pesannya dikirim.",
    seconds: (count: number) => `${String(count).replace(".", ",")} detik`,
    defaultMark: " (bawaan)",
    dictationAvailable: "Kata-katanya berasal dari pengenal suara perangkat ini sendiri; suaranya tidak pernah sampai ke bot kamu. Perangkat ini menyimpan pilihannya sendiri.",
    dictationUnavailable: "Browser ini tidak bisa mendikte, jadi mikrofon tidak muncul di sini. Chrome, Edge, dan Safari bisa, di halaman yang dibuka lewat https; aplikasi Android juga bisa.",

    gettingStarted: "Memulai",
    tour: "Tur",
    tourAbout: "Latihan beberapa menit: mengirim pesan, menjawab permintaan persetujuan, melihat Chief of Staff kamu membagi tugas, lalu merekrut bot kamu sendiri.",
    takeTour: "Ikuti tur",

    machine: "Mesin virtual",
    restart: "Mulai ulang",
    restarting: "Memulai ulang…",
    restartAbout: "Mencadangkan ruang kerja kamu, lalu menyalakan ulang mesinnya. File dan chat tetap tersimpan; apa pun yang sedang dikerjakan bot kamu akan terhenti.",
    restartConfirmTitle: "Mulai ulang mesin virtual kamu?",
    restartConfirmMessage: "Apa pun yang sedang dikerjakan bot kamu akan terhenti. File dan chat kamu tetap tersimpan.",
    restartLocalBefore: "Box ini berjalan di komputer kamu sendiri; mulai ulang di sana, dengan ",
    restartLocalBetween: " lalu ",
    restartLocalAfter: ".",

    reviewLead: "Beberapa tindakan diperiksa sebelum dijalankan. Aturan kamu di bawah memandu pemeriksaan itu; aturan ini berlaku untuk semua bot mulai tindakan berikutnya.",
    reviewEnabled: "Periksa tindakan sebelum dijalankan",
    reviewEnabledNote: "(mati: hanya batas keamanan tetap yang berlaku)",
    alwaysAllow: "Selalu izinkan",
    alwaysAllowNote: "(satu kalimat per baris; hal yang tidak perlu ditanyakan ke kamu)",
    alwaysAllowExample: "Push ke remote origin proyek saya boleh.\nMemasang paket dari npm boleh.",
    alwaysBlock: "Selalu blokir",
    alwaysBlockNote: "(satu kalimat per baris; hal yang harus selalu dibawa ke kamu)",
    alwaysBlockExample: "Jangan pernah mengirim email atas nama saya.\nJangan pernah menghapus branch git.",
    save: "Simpan",
    saved: "Tersimpan",

    skillsLead: "Prosedur yang diikuti setiap bot, dan yang milik satu bot saja.",
    skillsShared: "Bersama",
    skillsSharedAbout: "Setiap bot melihat ini. Taruh folder berisi SKILL.md ke",
    skillsEmptyShared: "Belum ada keahlian bersama.",
    skillsNewShared: "Keahlian bersama baru",
    skillsNone: "Belum ada keahlian. Tulis yang bersama di sini, atau yang milik satu bot dari panel detailnya.",
    skillsOpen: (skill: string) => `Ubah ${skill}`,
    skillsDeleteTitle: (skill: string) => `Hapus keahlian "${skill}"?`,
    skillsDeleteMessage: "Keahlian ini tidak lagi ada untuk diikuti. Tindakan ini tidak bisa dibatalkan.",
  },
);
